import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';

import {AuthFacade} from 'src/app/store/auth-state/+state/auth.facade';
import {
	BehaviorSubject,
	combineLatest,
	filter,
	interval,
	map,
	Subject,
	takeUntil
} from 'rxjs';

import {ManagerFacade} from 'src/app/store/manager/+state/manager.facade';
import {OrderStatus} from 'src/app/models/OrderStatus';
import {OrderType} from 'src/app/models/OrderType';
import * as moment from 'moment';
import {Socket} from 'ngx-socket-io';
import {SocketRole} from 'src/app/models/SocketRoles';
import Order from 'src/app/models/Order';
import {OrderService, STATUS_CHANGE} from 'src/app/services/order.service';
import {Table} from 'src/app/models/Table';
import {NavigationService} from 'src/app/services/navigation.service';
import {ArticleModalComponent} from 'src/app/components/article-modal/article-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {OrderPool} from 'src/app/models/OrderPool';
import {PreorderType} from 'src/app/models/PreorderType';
import {PrinterService} from 'src/app/services/printer.service';
import {Venue} from 'src/app/models/Venue';
import {DeliveryStatusDialogComponent} from 'src/app/components/delivery-status-dialog/delivery-status-dialog';

const STATUS_ORDER = [
	null,
	OrderStatus.AWAITING_CONFIRMATION,
	OrderStatus.IN_PREPARATION,
	OrderStatus.READY,
	OrderStatus.DONE
];

const ACTIVE_STATUSES = [
	OrderStatus.AWAITING_CONFIRMATION,
	OrderStatus.IN_PREPARATION,
	OrderStatus.READY
];

@Component({
	selector: 'app-manger',
	templateUrl: './manger.page.html',
	styleUrls: ['./manger.page.scss']
})
export class ManagerPage implements OnDestroy, OnInit {
	@ViewChild('scrollableContent', {static: false})
	scrollableContent!: ElementRef;
	private readonly onDestroy$ = new Subject<void>();
	private readonly stopListeningSocketForTable$ = new Subject<void>();
	public readonly updateStatusLoading$ =
		this.managerFacade.updateStatusLoading$;
	public venue$ = this.managerFacade.venue$;
	public venueData$ = new BehaviorSubject<Venue | null>(null);
	public orders$ = combineLatest([
		this.managerFacade.orders$,
		this.managerFacade.ordersPool$,
		this.managerFacade.orderStatusFiler$
	]).pipe(
		map(([ordersValue, poolValue, status]) => {
			let orders = structuredClone(ordersValue).filter(
				o => o?.preorder?.type !== PreorderType.FOOD_SPOT
			);

			let pools = structuredClone(poolValue) as OrderPool[];

			const today = moment().startOf('day');

			// today OR asap
			orders = orders.filter(
				o => o.asap || moment(o.orderAt).isSame(today, 'day')
			);

			// remove DONE unless DONE filter selected
			if (status !== OrderStatus.DONE) {
				orders = orders.filter(o => o.status !== OrderStatus.DONE);
				pools = pools.filter(p => p.orderStatus !== OrderStatus.DONE);
			}

			// ===== ACTIVE MODE =====
			if (!status) {
				orders = orders.filter(
					o => o.status != null && ACTIVE_STATUSES.includes(o.status)
				);

				pools = pools
					.filter(p => ACTIVE_STATUSES.includes(p.orderStatus))
					.map(p => ({
						...p,
						orders: p.orders.filter(
							o => o.status != null && ACTIVE_STATUSES.includes(o.status)
						)
					}));

				return [...pools, ...orders]
					.sort(this.sortByTime)
					.sort(this.sortByStatus);
			}

			// ===== STATUS MODE =====
			orders = orders.filter(o => o.status === status);
			pools = pools.filter(p => p.orderStatus === status);

			return [...pools, ...orders]
				.sort(this.sortByTime)
				.sort(this.sortByStatus);
		})
	);

	public statusChangeENUM = STATUS_CHANGE;

	private previousUberStatus = false;
	private previousWoltStatus = false;
	public statusUber$ = this.managerFacade.statusUber$;
	public statusWolt$ = this.managerFacade.statusWolt$;

	constructor(
		private authFacade: AuthFacade,
		private managerFacade: ManagerFacade,
		private socket: Socket,
		private orderService: OrderService,
		private navService: NavigationService,
		public dialog: MatDialog,
		private printer: PrinterService
	) {}

	ngOnInit(): void {
		this.authFacade.userData$
			.pipe(
				filter(userValue => !!userValue),
				map(userData => {
					return userData?.venues[0] ?? null;
				}),
				takeUntil(this.onDestroy$)
			)
			.subscribe(venueId => {
				if (venueId) {
					this.loadDataByUser(venueId);
				}
			});
		this.venue$.pipe(takeUntil(this.onDestroy$)).subscribe(venue => {
			if (venue) {
				this.venueData$.next(venue);
			}

			if (venue?.externalOrderProviders) {
				if (venue?._id) {
					this.managerFacade.getStatusUber(venue._id);
				}
				if (venue?._id) {
					this.managerFacade.getStatusWolt(venue._id);
				}
			}
		});
		this.managerFacade.tables$
			.pipe(
				filter(device => device.length > 0),
				takeUntil(this.stopListeningSocketForTable$ || this.onDestroy$)
			)
			.subscribe(tables => {
				console.log('table', tables);
				if (tables.length > 0) {
					this.listenSocket(tables);
				}
			});

		this.pollStatuses();

		interval(180000) // every 3 minutes
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(() => this.pollStatuses());

		this.subscribeToStatusChanges();

		this.orders$.subscribe(v => console.log(v));
	}
	private loadDataByUser(venueId: string) {
		console.log(`loadDataByUser(venueId: ${venueId})`);
		this.managerFacade.loadVenueData({venueId});
		this.loadOrdersWithDefaultConfig(venueId);
	}
	ngOnDestroy() {
		this.onDestroy$.next();
	}
	private loadOrdersWithDefaultConfig(venueId: string) {
		console.log(`loadOrdersWithDefaultConfig(venueId: ${venueId})`);
		this.managerFacade.loadOrders({
			venue: venueId,
			status: [
				OrderStatus.AWAITING_CONFIRMATION,
				OrderStatus.IN_PREPARATION,
				OrderStatus.READY,
				OrderStatus.DONE
			],
			orderTypes: [
				OrderType.TERMINAL,
				OrderType.STANDARD,
				OrderType.VIRTUAL,
				OrderType.PREORDER
			],
			from: moment().subtract(1, 'days').toString(),
			to: moment().add(2, 'days').toString(),
			promoCode: true
		});
		this.managerFacade.loadOrdersPool({
			venue: venueId,
			status: [OrderStatus.IN_PREPARATION, OrderStatus.READY, OrderStatus.DONE],
			orderTypes: [
				OrderType.TERMINAL,
				OrderType.STANDARD,
				OrderType.VIRTUAL,
				OrderType.PREORDER
			],
			from: moment().startOf('day').toString(),
			to: moment().add(2, 'days').toString(),
			promoCode: true
		});
		this.listenSocketOrders(venueId);
	}
	private listenSocketOrders(venueId: string) {
		console.log(`listenSocketOrders(venueId: ${venueId}) `);
		this.socket.removeAllListeners();
		this.socket.on('connect', () => {
			this.authenticateSocket({
				roles: [SocketRole.COUNTER_DISPLAY, SocketRole.COUNTER_DISPLAY_MAIN],
				venueIds: [venueId]
			});
		});

		this.socket.on('venue-' + venueId + '-orders', (data: any) => {
			if (data?.order && data?.type == 'update') {
				console.log('LISTEN', data);
				this.updateState(data.order);
			}
		});
		this.socket.on('venue-' + venueId + '-order-pools', (data: any) => {
			console.log('LISTEN pools', data);

			if (data?.orderPool && data?.type == 'updated') {
				console.log('LISTEN pools', data);
				this.managerFacade.addOrderPoolNew(data?.orderPool);
			}
		});
	}
	private async updateState(order: Order) {
		this.managerFacade.updateOrder(order);
		try {
			console.log(
				'UPDATE STATE',
				order.code,
				order.status,
				this.printer.automatic
			);
			if (
				order?.status === OrderStatus.IN_PREPARATION &&
				this.printer.automatic
			) {
				const venue = this.venueData$.getValue();
				if (this.printer && this.printer?.isConnected && venue) {
					this.printer.print(order, venue);
				}
			}
		} catch (e) {}
	}
	private authenticateSocket(data: {
		roles: SocketRole[];
		venueIds: string[];
	}): void {
		this.socket.emit('authenticate', data.roles, data.venueIds, (ack: any) => {
			console.log('Socket authenticated for ', ack, data);
		});
	}
	private listenSocket(tables: Table[]) {
		console.log('listenSocket(tables: Table[])');
		// tables.forEach(it => {
		// 	this.socket.on(
		// 		`terminalStatus_${it.venue}_${it.number.replace(' ', '_')}`,
		// 		(data: any) => {
		// 			console.log('EMIT STATUS', data);
		// 			this.managerFacade.updateTable({...it, status: data ?? 'Failed'});
		// 		}
		// 	);
		// });
	}
	updateStatusOrderPool(data: {orderPool: OrderPool; status: STATUS_CHANGE}) {
		console.log(
			'HERE UPDATE POOL',
			data,
			this.orderService.getNextOrderStatus(data.orderPool.orderStatus)
		);
		if (!data || !data.orderPool || !data.status) {
			return;
		}
		switch (data.status) {
			case STATUS_CHANGE.PREV:
				this.managerFacade.updateOrderPool(
					data.orderPool,
					this.orderService.getPreviousOrderStatus(data.orderPool.orderStatus)
				);
				break;
			case STATUS_CHANGE.NEXT:
				this.managerFacade.updateOrderPool(
					data.orderPool,
					this.orderService.getNextOrderStatus(data.orderPool.orderStatus)
				);
				break;
			default:
				break;
		}
	}
	updateStatus(data: {order: Order; status: STATUS_CHANGE}) {
		if (!data || !data.order || !data.status) {
			return;
		}
		switch (data.status) {
			case STATUS_CHANGE.PREV:
				//TODO: fix when needed
				// this.managerFacade.updateOrderStatus(
				// 	data.order,
				// 	this.orderService.getPreviousOrderStatus(data.order.status)
				// );
				break;
			case STATUS_CHANGE.NEXT:
				this.managerFacade.updateOrderStatus(
					data.order,
					this.orderService.getNextOrderStatus(data.order.status)
				);
				break;
			default:
				break;
		}
	}
	stockManager() {
		this.navService.stockManager();
	}

	private getStatusIndex = (item: Order | OrderPool): number => {
		if (this.checkIfOrder(item))
			return STATUS_ORDER.indexOf(item.status ?? null);
		if (this.checkIfOrderPool(item))
			return STATUS_ORDER.indexOf(item.orderStatus ?? null);
		return -1;
	};

	private getOrderTime = (item: Order | OrderPool): number => {
		if (!this.checkIfOrder(item)) return 0;
		return moment(item.orderAt).valueOf();
	};

	private sortByStatus = (a: any, b: any) =>
		this.getStatusIndex(a) - this.getStatusIndex(b);
	private sortByTime = (a: any, b: any) =>
		this.getOrderTime(a) - this.getOrderTime(b);

	openDialog(): void {
		const dialogRef = this.dialog.open(ArticleModalComponent, {
			data: {articleName: 'aperol'}
		});

		dialogRef.afterClosed().subscribe(result => {
			console.log(result);
		});
	}
	checkIfOrder(value: Order | OrderPool): value is Order {
		return (value as Order)?.orderedArticles != undefined;
	}
	checkIfOrderPool(value: Order | OrderPool): value is OrderPool {
		return (value as OrderPool)?.dndCompany != undefined;
	}

	scrollLeft() {
		this.scrollableContent.nativeElement.scrollBy({
			left: -500,
			behavior: 'smooth'
		});
	}

	scrollRight() {
		this.scrollableContent.nativeElement.scrollBy({
			left: 500,
			behavior: 'smooth'
		});
	}

	private pollStatuses() {
		const id = this.venueData$.getValue()?._id;

		if (!id || !this.venueData$.getValue()?.externalOrderProviders) {
			return;
		}

		this.managerFacade.getStatusUber(id);
		this.managerFacade.getStatusWolt(id);
	}

	private subscribeToStatusChanges() {
		this.statusUber$.pipe(takeUntil(this.onDestroy$)).subscribe(status => {
			this.checkChange('uber', status.isActive);
		});

		this.statusWolt$.pipe(takeUntil(this.onDestroy$)).subscribe(status => {
			this.checkChange('wolt', status.isActive);
		});
	}

	private checkChange(provider: 'uber' | 'wolt', newStatus: boolean) {
		let previousStatus =
			provider === 'uber' ? this.previousUberStatus : this.previousWoltStatus;

		if (previousStatus === true && newStatus === false) {
			this.showStatusDialog(provider);
		}

		if (provider === 'uber') {
			this.previousUberStatus = newStatus;
		} else {
			this.previousWoltStatus = newStatus;
		}
	}

	private showStatusDialog(provider: 'uber' | 'wolt') {
		const providerText = provider === 'uber' ? 'Uber Eats' : 'Wolt';

		this.dialog.open(DeliveryStatusDialogComponent, {
			data: {providerText}
		});
	}

	public checkIfSetupOrderProviders(
		venue: Venue,
		orderProvider: 'wolt' | 'uber_eat' | 'self-delivery' | 'jet'
	): boolean {
		if (orderProvider === 'wolt') {
			return venue.externalOrderProviders?.wolt?.venue ? true : false;
		}
		if (orderProvider === 'self-delivery') {
			return (venue?.deliveryHours ?? []).length > 0 ? true : false;
		}
		if (orderProvider === 'uber_eat') {
			return venue.externalOrderProviders?.uberEats?.storeId ? true : false;
		}
		if (orderProvider === 'jet') {
			return venue.externalOrderProviders?.jet?.enabled;
		}

		return true;
	}
}
